import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export interface ExpandableContainerProps {
  /**
   * The content to be displayed in the container
   */
  children: React.ReactNode;
  /**
   * Maximum height of the container when collapsed (in pixels)
   */
  maxHeight?: number;
  /**
   * Custom className for the container
   */
  className?: string;
  /**
   * Custom className for the content wrapper
   */
  contentClassName?: string;
  /**
   * Custom className for the expand/collapse button
   */
  buttonClassName?: string;
  /**
   * Text to display when the container is collapsed
   */
  expandText?: string;
  /**
   * Text to display when the container is expanded
   */
  collapseText?: string;
}

export function ExpandableContainer({
  children,
  maxHeight = 100,
  className,
  contentClassName,
  buttonClassName,
  expandText = "Show More",
  collapseText = "Show Less",
}: ExpandableContainerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // naturalHeight is null until measured. We use scrollHeight (layout pixels)
  // instead of getBoundingClientRect().height, which returns zoom-scaled screen
  // pixels inside a ReactFlow canvas and causes the wrong collapsed/expanded
  // heights to be applied at zoom levels other than 1.
  const [naturalHeight, setNaturalHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => setNaturalHeight(el.scrollHeight);
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isTall = naturalHeight !== null && naturalHeight > maxHeight;

  // The outer div height follows this logic:
  //   • Before measurement (naturalHeight === null): height = maxHeight
  //     This ensures the node starts at the correct small size so that ReactFlow
  //     never measures a full-height node and gets stuck at that stale value.
  //   • Measured as short content: height = undefined (no constraint)
  //   • Measured as tall, collapsed: height = maxHeight
  //   • Measured as tall, expanded: height = naturalHeight (full content)
  const outerHeight =
    naturalHeight !== null && !isTall
      ? undefined
      : isExpanded
        ? (naturalHeight ?? maxHeight)
        : maxHeight;

  return (
    <div className={cn("relative", className)}>
      <div
        className={cn(
          "overflow-hidden transition-all duration-300",
          "relative",
          contentClassName,
        )}
        style={{
          height: outerHeight,
          ...(isTall &&
            !isExpanded && {
              mask: `linear-gradient(to bottom, black 0%, black calc(100% - 100px), transparent 100%)`,
              WebkitMask: `linear-gradient(to bottom, black 0%, black calc(100% - 100px), transparent 100%)`,
            }),
        }}
      >
        {/*
          Small padding ensures margin collapsing doesn't corrupt scrollHeight.
        */}
        <div ref={contentRef} className="pb-[1px]">
          {children}
        </div>
      </div>
      {isTall && (
        <Button
          variant="outline"
          size="sm"
          className={cn("w-full mt-2 text-xs", buttonClassName)}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? (
            <>
              <ChevronUp />
              {collapseText}
            </>
          ) : (
            <>
              <ChevronDown />
              {expandText}
            </>
          )}
        </Button>
      )}
    </div>
  );
}

export default ExpandableContainer;
